/* import __COLOCATED_TEMPLATE__ from './conversation-options.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type Conversation from 'embercom/objects/inbox/conversation';
import { NewConversation } from 'embercom/objects/inbox/conversation';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type InboxState from 'embercom/services/inbox-state';
import { HotkeyID } from 'embercom/services/inbox-hotkeys/HotkeyID';
import type Session from 'embercom/services/session';
import type Router from '@ember/routing/router-service';
import {
  type ReplyChannel,
  replyChannelIsMultiParticipant,
  ReplyChannelMap,
} from 'embercom/objects/inbox/composer-pane';
import { tracked } from '@glimmer/tracking';
import type CommandKService from 'embercom/services/command-k';
import { Channel } from 'embercom/models/data/inbox/channels';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import type TeammateAiTranslationSettings from 'embercom/services/teammate-ai-translation-settings';
import type CopilotUsageService from 'embercom/services/copilot-usage-service';
import { TRANSLATIONS_FREE_CREDIT_LIMIT } from 'embercom/services/copilot-usage-service';
import { canExportConversationPdfInbox2 } from 'embercom/lib/settings/permissions/inline-permission-enforcement';

interface Args {
  conversation: Conversation | NewConversation;
  hideConversationEvents: boolean;
  onToggleConversationEvents: () => unknown;
  exportConversation: () => void;
  exportConversationPdf?: () => void;
  allowManageParticipants?: boolean;
}

interface Signature {
  Args: Args;
}

enum DropdownOption {
  newConversation = 'new-conversation',
  conversationEvents = 'conversation-events',
  exportConversationText = 'export-conversation-text',
  exportConversationPdf = 'export-conversation-pdf',
  manageParticipants = 'manage-participants',
  mergeInto = 'merge-into',
  translateConversation = 'translate-conversation',
}

type MoreOptionItem = {
  text?: string;
  badge?: { klass: string; args?: Record<string, unknown> };
  id: DropdownOption | string;
  isSelected: boolean;
  isDisabled: boolean;
  icon?: string;
  iconSet?: string;
  hotkeyId?: HotkeyID;
  tooltipText?: string;
  tooltipContent?: { klass: string; args?: Record<string, unknown> };
};

const CROSS_USER_MERGEABLE_CHANNELS = [Channel.Email, Channel.PhoneCall];

abstract class ConversationOption {
  conversationOrTicket: Conversation;
  session: Session;
  intl: IntlService;

  protected constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    this.conversationOrTicket = conversationOrTicket;
    this.session = session;
    this.intl = intl;
  }

  get isTicket() {
    return this.conversationOrTicket.isTicket;
  }

  get isUnreplyableTicket() {
    return this.isTicket && !this.conversationOrTicket.isCustomerTicket;
  }

  abstract get id(): DropdownOption;
  abstract get icon(): string;

  get text(): string | undefined {
    return undefined;
  }

  get badge(): { klass: string; args?: Record<string, unknown> } | undefined {
    return undefined;
  }

  get iconSet(): string | undefined {
    return undefined;
  }

  get hotkeyId(): HotkeyID | undefined {
    return undefined;
  }

  get tooltipText(): string | undefined {
    return undefined;
  }

  get tooltipContent(): { klass: string; args?: Record<string, unknown> } | undefined {
    return undefined;
  }

  get isSelected(): boolean {
    return false;
  }

  get isDisabled(): boolean {
    return false;
  }

  get isVisible(): boolean {
    return true;
  }

  get isVisibleOnCmdK(): boolean {
    return this.isVisible;
  }

  protected get isVisibleInCurrentInbox(): boolean {
    if (this.session.workspace.isStandaloneApp) {
      return true;
    }
    return !this.session.showLightInbox;
  }

  toOptionItem(): MoreOptionItem {
    return {
      id: this.id,
      text: this.text,
      badge: this.badge,
      isSelected: this.isSelected,
      isDisabled: this.isDisabled,
      icon: this.icon,
      iconSet: this.iconSet,
      hotkeyId: this.hotkeyId,
      tooltipText: this.tooltipText,
      tooltipContent: this.tooltipContent,
    };
  }
}

class ManageParticipantsOption extends ConversationOption {
  allowManageParticipants: boolean | undefined;
  replyChannelIsMultiParticipant: (replyChannel?: ReplyChannel) => boolean;

  constructor(
    conversationOrTicket: Conversation,
    session: Session,
    intl: IntlService,
    allowManageParticipants: boolean | undefined,
    replyChannelIsMultiParticipant: (replyChannel?: ReplyChannel) => boolean,
  ) {
    super(conversationOrTicket, session, intl);
    this.allowManageParticipants = allowManageParticipants;
    this.replyChannelIsMultiParticipant = replyChannelIsMultiParticipant;
  }

  get id() {
    return DropdownOption.manageParticipants;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.manage-participants');
  }

  get icon() {
    return 'multiple-people';
  }

  get iconSet() {
    return 'standard';
  }

  get isVisible() {
    if (this.session.showLightInbox) {
      return false;
    }

    if (this.isUnreplyableTicket) {
      return false;
    }

    let replyChannel = ReplyChannelMap[this.conversationOrTicket.channel.replyChannel];

    return (
      this.allowManageParticipants === true &&
      this.replyChannelIsMultiParticipant(replyChannel) &&
      this.conversationOrTicket.channel.current !== Channel.Slack &&
      this.conversationOrTicket.channel.current !== Channel.Discord
    );
  }
}

class NewConversationOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.newConversation;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.new-conversation');
  }

  get icon() {
    return 'compose';
  }

  get iconSet() {
    return 'standard';
  }

  get isVisible() {
    if (this.session.showLightInbox) {
      return false;
    }

    if (this.isUnreplyableTicket) {
      return false;
    }

    return true;
  }
}

class MergeIntoOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.mergeInto;
  }

  get text() {
    return this.intl.t('inbox.conversation-header.merge-into');
  }

  get icon() {
    return 'merge';
  }

  get hotkeyId() {
    return HotkeyID.MergeInto;
  }

  get tooltipText() {
    if (this.hasMultipleParticipants && !this.isCrossUserMergeAllowed) {
      let translationKey = this.isTicket
        ? 'inbox.conversation-header.merge-into-disabled.ticket-multiple-participants'
        : 'inbox.conversation-header.merge-into-disabled.conversation-multiple-participants';

      return this.intl.t(translationKey);
    }

    if (this.isUnreplyableTicket) {
      return this.intl.t('inbox.conversation-header.merge-into-disabled.ticket-not-customer');
    }

    return undefined;
  }

  get isDisabled() {
    if (this.hasMultipleParticipants && !this.isCrossUserMergeAllowed) {
      return true;
    }

    if (this.isUnreplyableTicket) {
      return true;
    }

    return false;
  }

  get isVisible() {
    if (this.session.showLightInbox) {
      return false;
    }

    if (this.conversationOrTicket.channel.current === Channel.Discord) {
      return false;
    }

    return true;
  }

  get isVisibleOnCmdK() {
    if (this.conversationOrTicket.channel.current === Channel.Discord) {
      return false;
    }

    if (this.isUnreplyableTicket) {
      return false;
    }

    if (!this.isCrossUserMergeAllowed && this.hasMultipleParticipants) {
      return false;
    }

    return true;
  }

  get hasMultipleParticipants() {
    return this.conversationOrTicket.participantSummaries.length > 1;
  }

  get isCrossUserMergeAllowed(): boolean {
    return (
      this.session.workspace.enabledCrossUserConversationMerge &&
      CROSS_USER_MERGEABLE_CHANNELS.includes(this.conversationOrTicket?.channel?.initial)
    );
  }
}

class ExportConversationTextOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.exportConversationText;
  }

  get text() {
    return this.isTicket
      ? this.intl.t('inbox.conversation-header.export-ticket-text')
      : this.intl.t('inbox.conversation-header.export-conversation-text');
  }

  get icon() {
    return 'export';
  }

  get isVisible() {
    return this.isVisibleInCurrentInbox;
  }
}

class ExportConversationPdfOption extends ConversationOption {
  constructor(conversationOrTicket: Conversation, session: Session, intl: IntlService) {
    super(conversationOrTicket, session, intl);
  }

  get id() {
    return DropdownOption.exportConversationPdf;
  }

  get text() {
    return this.isTicket
      ? this.intl.t('inbox.conversation-header.export-ticket-pdf')
      : this.intl.t('inbox.conversation-header.export-conversation-pdf');
  }

  get icon() {
    return 'export';
  }

  get isVisible() {
    if (!canExportConversationPdfInbox2(this.session)) {
      return false;
    }

    return this.isVisibleInCurrentInbox;
  }
}

class ShowEventsOption extends ConversationOption {
  hideEvents: boolean;

  constructor(
    conversationOrTicket: Conversation,
    session: Session,
    intl: IntlService,
    hideEvents: boolean,
  ) {
    super(conversationOrTicket, session, intl);

    this.hideEvents = hideEvents;
  }

  get id() {
    return DropdownOption.conversationEvents;
  }

  get text() {
    return this.isTicket
      ? this.intl.t('inbox.conversation-header.ticket-events')
      : this.intl.t('inbox.conversation-header.conversation-events');
  }

  get icon() {
    return 'visible';
  }

  get hotkeyId() {
    return HotkeyID.ToggleEvents;
  }

  get isSelected() {
    return !this.hideEvents;
  }
}

class TranslateConversationOption extends ConversationOption {
  conversationTranslationSettings: ConversationTranslationSettings;
  teammateAiTranslationSettings: TeammateAiTranslationSettings;
  hasTranslationsLeft: boolean;
  copilotUsageService: CopilotUsageService;

  constructor(
    conversationOrTicket: Conversation,
    session: Session,
    intl: IntlService,
    conversationTranslationSettings: ConversationTranslationSettings,
    teammateAiTranslationSettings: TeammateAiTranslationSettings,
    copilotUsageService: CopilotUsageService,
  ) {
    super(conversationOrTicket, session, intl);

    this.conversationTranslationSettings = conversationTranslationSettings;
    this.teammateAiTranslationSettings = teammateAiTranslationSettings;
    this.copilotUsageService = copilotUsageService;
    this.hasTranslationsLeft = this.copilotUsageService.hasTranslationsLeft(
      this.session.teammate,
      this.conversationOrTicket,
    );
  }

  get id() {
    return DropdownOption.translateConversation;
  }

  get icon() {
    return 'language';
  }

  get text() {
    return this.intl.t('inbox.conversation-header.translations.translate-conversation');
  }

  get badge() {
    if (!this.session.workspace.isFeatureEnabled('multilingual-ai-translation-pricing')) {
      return undefined;
    }

    if (this.session.workspace.isStandaloneApp) {
      return undefined;
    }

    return {
      klass: 'inbox2/translate-conversation-dropdown',
      args: {
        hideLimitIndicator: this.session.teammate.hasUnlimitedCopilotAccess,
        hasTranslationsLeft: this.hasTranslationsLeft,
        quota: {
          used: this.copilotUsageService.getAdminUsedTranslationsCountFromConversation(
            this.conversationOrTicket,
          ),
          available: TRANSLATIONS_FREE_CREDIT_LIMIT,
        },
      },
    };
  }

  get conversationLanguage(): string {
    let conversationLanguage =
      this.conversationOrTicket.getAttributeForDescriptor('language')?.value;
    if (typeof conversationLanguage !== 'string') {
      return this.session.workspace.locale || '';
    }
    return conversationLanguage;
  }

  get conversationLanguageIsExcluded() {
    return this.teammateAiTranslationSettings.excludedLanguages.includes(this.conversationLanguage);
  }

  get isSelected() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.conversationOrTicket,
    );
  }

  get isVisible() {
    if (!this.conversationTranslationSettings.autoTranslationEnabled) {
      return false;
    }
    if (!this.conversationLanguage) {
      return false;
    }

    let workspaceDefaultLanguage = this.session.workspace.locale;
    let isConversationLanguageSameAsWorkspaceDefaultLanguage =
      this.conversationLanguage === workspaceDefaultLanguage;
    if (isConversationLanguageSameAsWorkspaceDefaultLanguage) {
      return false;
    }

    if (this.session.workspace.isFeatureEnabled('multilingual-ai-translation-pricing')) {
      if (!this.session.teammate.hasCopilotAccess) {
        return false;
      }
    }

    return true;
  }

  get isDisabled() {
    if (!this.session.workspace.isFeatureEnabled('multilingual-ai-translation-pricing')) {
      return false;
    }

    if (this.conversationLanguageIsExcluded) {
      return true;
    }

    return !this.conversationTranslationSettings.hasTranslationAccessForConversation(
      this.conversationOrTicket,
    );
  }

  get tooltipContent() {
    if (!this.session.workspace.isFeatureEnabled('multilingual-ai-translation-pricing')) {
      return undefined;
    }

    if (this.session.workspace.isStandaloneApp) {
      return undefined;
    }

    if (this.conversationLanguageIsExcluded) {
      return {
        klass: 'inbox2/translate-conversation-skip-languages-tooltip',
        args: {
          conversationLanguage: this.conversationLanguage,
        },
      };
    }

    let hasUnlimitedCopilotAccess = this.session.teammate.hasUnlimitedCopilotAccess;
    if (hasUnlimitedCopilotAccess) {
      return undefined;
    }

    return {
      klass: 'inbox2/translate-conversation-tooltip',
      args: {
        isLimitReached: !this.hasTranslationsLeft,
        quota: {
          used: this.copilotUsageService.getAdminUsedTranslationsCountFromConversation(
            this.conversationOrTicket,
          ),
          available: TRANSLATIONS_FREE_CREDIT_LIMIT,
        },
      },
    };
  }
}

export default class Inbox2ConversationOptionDropdownComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @service declare inboxState: InboxState;
  @service declare session: Session;
  @service declare router: Router;
  @service declare commandK: CommandKService;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare teammateAiTranslationSettings: TeammateAiTranslationSettings;
  @service declare copilotUsageService: CopilotUsageService;

  @tracked isManagingParticipants = false;

  get isNewConversation() {
    return this.args.conversation instanceof NewConversation;
  }
  get moreOptionItems(): MoreOptionItem[] {
    if (this.isNewConversation) {
      return [];
    }

    let options: ConversationOption[] = [
      new ManageParticipantsOption(
        this.conversation,
        this.session,
        this.intl,
        this.args.allowManageParticipants,
        replyChannelIsMultiParticipant,
      ),
      new MergeIntoOption(this.conversation, this.session, this.intl),
      new NewConversationOption(this.conversation, this.session, this.intl),
      new ExportConversationTextOption(this.conversation, this.session, this.intl),
      new ExportConversationPdfOption(this.conversation, this.session, this.intl),
      new ShowEventsOption(
        this.conversation,
        this.session,
        this.intl,
        this.args.hideConversationEvents,
      ),
      new TranslateConversationOption(
        this.conversation,
        this.session,
        this.intl,
        this.conversationTranslationSettings,
        this.teammateAiTranslationSettings,
        this.copilotUsageService,
      ),
    ];

    return options.filter((option) => option.isVisible).map((option) => option.toOptionItem());
  }

  get conversation(): Conversation {
    return this.args.conversation as Conversation;
  }

  get showManagingParticipantsCmdKAction() {
    if (this.isNewConversation) {
      return false;
    }

    return new ManageParticipantsOption(
      this.conversation,
      this.session,
      this.intl,
      this.args.allowManageParticipants,
      replyChannelIsMultiParticipant,
    ).isVisibleOnCmdK;
  }

  get showMergeIntoCmdKAction() {
    if (this.isNewConversation) {
      return false;
    }

    return new MergeIntoOption(this.conversation, this.session, this.intl).isVisibleOnCmdK;
  }

  @action showManageParticipantsModal() {
    this.isManagingParticipants = true;
  }

  @action showMergeIntoModal() {
    this.commandK.registerAndShow({
      actionID: 'merge-into',
      onSelect: () => {},
    });
  }

  @action onMoreOptionsSelectionChange(item: MoreOptionItem) {
    switch (item.id) {
      case DropdownOption.newConversation:
        this.router.transitionTo('inbox.workspace.inbox.new-conversation', {
          queryParams: {
            recipient: this.conversation.userSummary.id,
            ticketTypeId: undefined,
          },
        });
        break;
      case DropdownOption.conversationEvents:
        this.args.onToggleConversationEvents();
        this.intercomEventService.trackAnalyticsEvent({
          object: 'visibility',
          action: 'changed',
          section: 'conversation_header',
          conversation_id: this.conversation.id,
          visibility_type: 'events',
          is_visible: !this.args.hideConversationEvents,
        });
        break;
      case DropdownOption.mergeInto:
        this.showMergeIntoModal();
        this.intercomEventService.trackAnalyticsEvent({
          object: 'merge_into',
          action: 'clicked',
          section: 'conversation_header',
          conversation_id: this.conversation.id,
          using_modal: true,
        });
        break;
      case DropdownOption.exportConversationText:
        this.args.exportConversation();
        break;
      case DropdownOption.exportConversationPdf:
        if (this.args.exportConversationPdf) {
          this.args.exportConversationPdf();
        }
        break;
      case DropdownOption.translateConversation: {
        this.conversationTranslationSettings.toggleAutoTranslationEnabledForConversation(
          this.conversation.id,
        );
        break;
      }
      case DropdownOption.manageParticipants:
        this.showManageParticipantsModal();

        this.intercomEventService.trackAnalyticsEvent({
          object: 'manage_participants',
          action: 'clicked',
          section: 'conversation_header',
          conversation_id: this.conversation.id,
          using_modal: true,
        });
        break;
      default:
        break;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationOptions': typeof Inbox2ConversationOptionDropdownComponent;
    'inbox2/conversation-options': typeof Inbox2ConversationOptionDropdownComponent;
  }
}
